import { AllowedAction } from './allowedActions'
import {
  LayoutDashboard,
  Building,
  Building2,
  Users,
  Settings,
  FileKey,
  UserCog,
  LayoutGrid,
  UserRound,
  Monitor,
  FileText,
  Smartphone,
  ScrollText,
  Film,
  Package,
  Wrench,
  CalendarClock,
  Image,
  Images,
  SlidersHorizontal,
  Tags,
} from 'lucide-react'

export type ScopeType = 'provider' | 'clinic' | 'ad_supplier'

export interface MenuItemData {
  label: string
  icon: React.ElementType
  onClick: (navigate: (path: string) => void) => void
  path: string
  children?: MenuItemData[]
  showForRoles?: ('sys_admin' | 'investor')[] // 只用於 sys_admin 和 investor
  showForScopes?: ScopeType[] // 根據用戶關聯判斷顯示
  permission?: {
    module: string
    action: AllowedAction
  }
  /**
   * 嚴格主權項目：跳過 sys_admin god-view 與階層繼承，只認上面 permission 本身。
   * 用於 org 內部角色管理——後端對這些端點的 gate 只放行「擁有該組織的 provider 管理權」，
   * sys_admin 與下層一律 403（matrix X）。設 true 讓 menu 與後端 gate 完全一致，
   * 不會出現「看得到但點了 403」。見 apps/admin-api/src/routes/roles。
   */
  exactPermission?: boolean
}

export type ViewMode = 'default' | 'clinic' | 'ad' | 'provider' | 'clinic_group' | 'ad_supplier_group'

// ==========================================
// Admin Mode (sys_admin 後台管理) — paths under /admin/*
// 使用 SystemPermissionModule (Prisma enum key 格式)
// 也用作 mode='default'（例如 investor 在 /investor/clinics）的 sidebar；filter 會依 role 顯示對應項目
// ==========================================
export const adminMenuData: MenuItemData[] = [
  {
    label: '首頁',
    icon: LayoutDashboard,
    onClick: (navigate) => navigate('/admin'),
    path: '/admin',
    showForRoles: ['sys_admin'],
  },
  {
    label: '組織列表',
    icon: Building2,
    onClick: (navigate) => navigate('/admin/providers'),
    path: '/admin/providers',
    showForRoles: ['sys_admin'],
    permission: {
      module: 'system_provider',
      action: AllowedAction.R,
    },
  },
  {
    label: '使用者管理',
    icon: Users,
    onClick: (navigate) => navigate('/admin/users'),
    path: '/admin/users',
    showForRoles: ['sys_admin'],
    permission: {
      module: 'system_user',
      action: AllowedAction.R,
    },
  },
  {
    label: '角色管理',
    icon: FileKey,
    onClick: (navigate) => navigate('/admin/user-roles'),
    path: '/admin/user-roles',
    showForRoles: ['sys_admin'],
    permission: {
      module: 'system_user_role',
      action: AllowedAction.R,
    },
  },
  {
    label: 'APP版本管理',
    icon: Settings,
    onClick: (navigate) => navigate('/admin/system-versions'),
    path: '/admin/system-versions',
    showForRoles: ['sys_admin'],
    permission: {
      module: 'system_system_version',
      action: AllowedAction.R,
    },
  },
  {
    label: '介接廠商管理',
    icon: Users,
    onClick: (navigate) => navigate('/admin/integration-vendors'),
    path: '/admin/integration-vendors',
    showForRoles: ['sys_admin'],
    permission: {
      module: 'system_integration_vendor',
      action: AllowedAction.R,
    },
  },
  {
    label: '操作日誌',
    icon: ScrollText,
    onClick: (navigate) => navigate('/admin/audit-logs'),
    path: '/admin/audit-logs',
    showForRoles: ['sys_admin'],
    permission: {
      module: 'system_audit_log',
      action: AllowedAction.R,
    },
  },
  {
    label: '診所統計',
    icon: Building,
    onClick: (navigate) => navigate('/investor/clinics'),
    path: '/investor/clinics',
    showForRoles: ['investor'],
    permission: {
      module: 'system_clinic',
      action: AllowedAction.R,
    },
  },
  {
    label: '系統工具',
    icon: Wrench,
    onClick: (navigate) => navigate('/admin/system-tools'),
    path: '/admin/system-tools',
    showForRoles: ['sys_admin'],
  },
]

// ==========================================
// Provider Mode (組織管理)
// provider_admin 進入某個組織後的選單
// 使用 ProviderUserPermissionModule (Prisma enum key 格式)
// ==========================================
const providerMenuData: MenuItemData[] = [
  {
    label: '首頁',
    icon: LayoutDashboard,
    onClick: (navigate) => navigate('/'),
    path: '/',
  },
  {
    label: '組織資料',
    icon: Building2,
    onClick: (navigate) => navigate('/provider/info'),
    path: '/provider/info',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_provider_settings',
      action: AllowedAction.R,
    },
  },
  {
    label: '診所列表',
    icon: Building,
    onClick: (navigate) => navigate('/provider/clinics'),
    path: '/provider/clinics',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider', 'clinic'],
    permission: {
      module: 'provider_clinic_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '使用者管理',
    icon: Users,
    onClick: (navigate) => navigate('/provider/users'),
    path: '/provider/users',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_user_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '廣告商列表',
    icon: Users,
    onClick: (navigate) => navigate('/provider/ad-suppliers'),
    path: '/provider/ad-suppliers',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_supplier_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '診所集團',
    icon: Building2,
    onClick: (navigate) => navigate('/provider/clinic-groups'),
    path: '/provider/clinic-groups',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_clinic_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '廣告商集團',
    icon: Users,
    onClick: (navigate) => navigate('/provider/ad-supplier-groups'),
    path: '/provider/ad-supplier-groups',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_supplier_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '角色管理',
    icon: FileKey,
    onClick: (navigate) => navigate('/provider/user-roles'),
    path: '/provider/user-roles',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_user_role',
      action: AllowedAction.R,
    },
    // 後端 /api/providers/:id/roles gate = provider::user_role（sys_admin 也擋）。
    exactPermission: true,
  },
  {
    label: '廣告排班模版',
    icon: FileText,
    onClick: (navigate) => navigate('/provider/ad-schedule-templates'),
    path: '/provider/ad-schedule-templates',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_ad_schedule',
      action: AllowedAction.R,
    },
  },
  {
    label: '廣告圖片批次',
    icon: Images,
    onClick: (navigate) => navigate('/provider/ad-image-batches'),
    path: '/provider/ad-image-batches',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_ad_schedule',
      action: AllowedAction.R,
    },
  },
  {
    label: 'Banner 引擎配置',
    icon: SlidersHorizontal,
    onClick: (navigate) => navigate('/provider/banner-config'),
    path: '/provider/banner-config',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_ad_schedule',
      action: AllowedAction.R,
    },
  },
  {
    label: '裝置管理',
    icon: Smartphone,
    onClick: (navigate) => navigate('/provider/device-management'),
    path: '/provider/device-management',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_broadcast_device',
      action: AllowedAction.R,
    },
  },
  {
    label: '連線狀態快照',
    icon: Monitor,
    onClick: (navigate) => navigate('/provider/connection-status-snapshots'),
    path: '/provider/connection-status-snapshots',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_broadcast_device',
      action: AllowedAction.R,
    },
  },
  {
    label: '戰情室',
    icon: Monitor,
    onClick: (navigate) => navigate('/provider/war-room'),
    path: '/provider/war-room',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_war_room',
      action: AllowedAction.R,
    },
  },
  {
    label: '操作日誌',
    icon: ScrollText,
    onClick: (navigate) => navigate('/provider/audit-logs'),
    path: '/provider/audit-logs',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_audit_log',
      action: AllowedAction.R,
    },
  },
  {
    label: '工具包',
    icon: Package,
    onClick: (navigate) => navigate('/provider/apps/toolkit'),
    path: '/provider/apps/toolkit',
    showForRoles: ['sys_admin'],
    showForScopes: ['provider'],
    permission: {
      module: 'provider_toolkit',
      action: AllowedAction.R,
    },
  },
]

// ==========================================
// Clinic Mode (診所管理)
// 進入特定診所後的選單，平面選單無子層
// 使用 ClinicUserPermissionModule (Prisma enum key 格式)
// ==========================================
const clinicMenuData: MenuItemData[] = [
  {
    label: '首頁',
    icon: LayoutDashboard,
    onClick: (navigate) => navigate('/'),
    path: '/',
  },
  {
    label: '資料管理',
    icon: LayoutGrid,
    onClick: (navigate) => navigate('/clinic/info'),
    path: '/clinic/info',
    permission: {
      module: 'clinic_clinic_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '使用者',
    icon: UserCog,
    onClick: (navigate) => navigate('/clinic/users'),
    path: '/clinic/users',
    permission: {
      module: 'clinic_user_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '角色管理',
    icon: FileKey,
    onClick: (navigate) => navigate('/clinic/user-roles'),
    path: '/clinic/user-roles',
    // 後端 /api/clinics/:id/roles gate = provider::clinic_management（org 主權：
    // 只有擁有診所的 provider 能管角色；診所自身/集團/sys_admin 一律 403）。
    permission: {
      module: 'provider_clinic_management',
      action: AllowedAction.R,
    },
    exactPermission: true,
  },
  {
    label: '醫生管理',
    icon: UserRound,
    onClick: (navigate) => navigate('/clinic/doctors'),
    path: '/clinic/doctors',
    permission: {
      module: 'clinic_clinic_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '診間管理',
    icon: Building,
    onClick: (navigate) => navigate('/clinic/rooms'),
    path: '/clinic/rooms',
    permission: {
      module: 'clinic_clinic_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '廣播裝置',
    icon: Monitor,
    onClick: (navigate) => navigate('/clinic/broadcast-devices'),
    path: '/clinic/broadcast-devices',
    permission: {
      module: 'clinic_broadcast_device',
      action: AllowedAction.R,
    },
  },
  {
    label: '模組管理',
    icon: Settings,
    onClick: (navigate) => navigate('/clinic/modules'),
    path: '/clinic/modules',
    permission: {
      module: 'clinic_clinic_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '叫號機管理',
    icon: Smartphone,
    onClick: (navigate) => navigate('/clinic/queue-machines'),
    path: '/clinic/queue-machines',
    permission: {
      module: 'clinic_queue_machine_management',
      action: AllowedAction.R,
    },
  },
]

// ==========================================
// Ad Supplier Mode (供應商管理)
// 進入特定供應商後的選單，平面選單無子層
// 使用 SupplierUserPermissionModule (Prisma enum key 格式)
// ==========================================
const adSupplierMenuData: MenuItemData[] = [
  {
    label: '首頁',
    icon: LayoutDashboard,
    onClick: (navigate) => navigate('/'),
    path: '/',
  },
  {
    label: '資料管理',
    icon: LayoutGrid,
    onClick: (navigate) => navigate('/ad-supplier'),
    path: '/ad-supplier',
    permission: {
      module: 'ad_supplier_supplier_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '使用者',
    icon: UserCog,
    onClick: (navigate) => navigate('/ad-supplier/users'),
    path: '/ad-supplier/users',
    permission: {
      module: 'ad_supplier_user_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '角色管理',
    icon: FileKey,
    onClick: (navigate) => navigate('/ad-supplier/user-roles'),
    path: '/ad-supplier/user-roles',
    // 後端 /api/ad-suppliers/:id/roles gate = provider::supplier_management。
    permission: {
      module: 'provider_supplier_management',
      action: AllowedAction.R,
    },
    exactPermission: true,
  },
  {
    label: '廣告管理',
    icon: Film,
    onClick: (navigate) => navigate('/ad-supplier/ads'),
    path: '/ad-supplier/ads',
    permission: {
      module: 'ad_supplier_ad_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '廣告圖片',
    icon: Image,
    onClick: (navigate) => navigate('/ad-supplier/ad-images'),
    path: '/ad-supplier/ad-images',
    permission: {
      module: 'ad_supplier_ad_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '品牌管理',
    icon: Tags,
    onClick: (navigate) => navigate('/ad-supplier/brands'),
    path: '/ad-supplier/brands',
    permission: {
      module: 'ad_supplier_ad_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '廣告排程',
    icon: CalendarClock,
    onClick: (navigate) => navigate('/ad-supplier/ad-schedules'),
    path: '/ad-supplier/ad-schedules',
    permission: {
      module: 'ad_supplier_ad_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '診所統計',
    icon: Building,
    onClick: (navigate) => navigate('/ad-supplier/clinics'),
    path: '/ad-supplier/clinics',
    permission: {
      module: 'ad_supplier_clinic_statistics',
      action: AllowedAction.R,
    },
  },
  {
    label: '統計報表',
    icon: FileText,
    onClick: (navigate) => navigate('/ad-supplier/reports'),
    path: '/ad-supplier/reports',
    permission: {
      module: 'ad_supplier_statistics_report',
      action: AllowedAction.R,
    },
  },
]

// ==========================================
// Clinic Group Mode (診所集團管理)
// 進入診所集團後的選單
// 使用 ClinicGroupUserPermissionModule (Prisma enum key 格式)
// ==========================================
const clinicGroupMenuData: MenuItemData[] = [
  {
    label: '首頁',
    icon: LayoutDashboard,
    onClick: (navigate) => navigate('/'),
    path: '/',
  },
  {
    label: '集團資料',
    icon: LayoutGrid,
    onClick: (navigate) => navigate('/clinic-group/info'),
    path: '/clinic-group/info',
    permission: {
      module: 'clinic_group_group_settings',
      action: AllowedAction.R,
    },
  },
  {
    label: '使用者',
    icon: UserCog,
    onClick: (navigate) => navigate('/clinic-group/users'),
    path: '/clinic-group/users',
    permission: {
      module: 'clinic_group_user_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '角色管理',
    icon: FileKey,
    onClick: (navigate) => navigate('/clinic-group/user-roles'),
    path: '/clinic-group/user-roles',
    // 後端 /api/clinic-groups/:id/roles gate = provider::clinic_group_management。
    permission: {
      module: 'provider_clinic_group_management',
      action: AllowedAction.R,
    },
    exactPermission: true,
  },
  {
    label: '診所列表',
    icon: Building,
    onClick: (navigate) => navigate('/clinic-group/clinics'),
    path: '/clinic-group/clinics',
    permission: {
      module: 'clinic_group_clinic_management',
      action: AllowedAction.R,
    },
  },
]

// ==========================================
// AdSupplier Group Mode (廣告商集團管理)
// 進入廣告商集團後的選單
// 使用 AdSupplierGroupUserPermissionModule (Prisma enum key 格式)
// ==========================================
const adSupplierGroupMenuData: MenuItemData[] = [
  {
    label: '首頁',
    icon: LayoutDashboard,
    onClick: (navigate) => navigate('/'),
    path: '/',
  },
  {
    label: '集團資料',
    icon: LayoutGrid,
    onClick: (navigate) => navigate('/ad-supplier-group/info'),
    path: '/ad-supplier-group/info',
    permission: {
      module: 'ad_supplier_group_group_settings',
      action: AllowedAction.R,
    },
  },
  {
    label: '使用者',
    icon: UserCog,
    onClick: (navigate) => navigate('/ad-supplier-group/users'),
    path: '/ad-supplier-group/users',
    permission: {
      module: 'ad_supplier_group_user_management',
      action: AllowedAction.R,
    },
  },
  {
    label: '角色管理',
    icon: FileKey,
    onClick: (navigate) => navigate('/ad-supplier-group/user-roles'),
    path: '/ad-supplier-group/user-roles',
    // 後端 /api/ad-supplier-groups/:id/roles gate = provider::ad_supplier_group_management。
    permission: {
      module: 'provider_ad_supplier_group_management',
      action: AllowedAction.R,
    },
    exactPermission: true,
  },
  {
    label: '供應商列表',
    icon: Users,
    onClick: (navigate) => navigate('/ad-supplier-group/suppliers'),
    path: '/ad-supplier-group/suppliers',
    permission: {
      module: 'ad_supplier_group_supplier_management',
      action: AllowedAction.R,
    },
  },
]


// ==========================================
// URL-based factories (viewMode → URL refactor)
// Inject the current scope id into every path so sidebar links
// resolve to `/clinic/:id/info` instead of `/clinic/info`.
// ==========================================

export function getClinicMenuData(clinicId: number): MenuItemData[] {
  return clinicMenuData.map((item) => transformMenuPath(item, '/clinic/', `/clinic/${clinicId}/`))
}

export function getProviderMenuData(providerId: number): MenuItemData[] {
  return providerMenuData.map((item) => transformMenuPath(item, '/provider/', `/provider/${providerId}/`))
}

export function getClinicGroupMenuData(groupId: number): MenuItemData[] {
  return clinicGroupMenuData.map((item) =>
    transformMenuPath(item, '/clinic-group/', `/clinic-group/${groupId}/`),
  )
}

export function getAdSupplierMenuData(supplierId: number): MenuItemData[] {
  return adSupplierMenuData.map((item) =>
    transformMenuPath(item, '/ad-supplier/', `/ad-supplier/${supplierId}/`),
  )
}

export function getAdSupplierGroupMenuData(groupId: number): MenuItemData[] {
  return adSupplierGroupMenuData.map((item) =>
    transformMenuPath(item, '/ad-supplier-group/', `/ad-supplier-group/${groupId}/`),
  )
}

function transformMenuPath(
  item: MenuItemData,
  oldPrefix: string,
  newPrefix: string,
): MenuItemData {
  const newPath = item.path.startsWith(oldPrefix)
    ? newPrefix + item.path.slice(oldPrefix.length)
    : item.path
  return {
    ...item,
    path: newPath,
    onClick: (navigate) => navigate(newPath),
    children: item.children?.map((c) => transformMenuPath(c, oldPrefix, newPrefix)),
  }
}
